import { ArrowForward } from '@mui/icons-material';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React, { JSX } from 'react';
import { FaLanguage, FaShippingFast, FaReceipt } from 'react-icons/fa';
import { GrReactjs } from 'react-icons/gr';
import {
  SiNextdotjs,
  SiNodedotjs,
  SiExpress,
  SiMongodb,
  SiStripe,
  SiTwilio,
  SiAmazon,
  SiDocker,
} from 'react-icons/si';
import { E_COMMERCE_STYLES } from './config';

// Technology data structure
const technologyData = {
  frontend: [
    {
      name: 'React',
      icon: GrReactjs,
      description:
        'Build fast-loading and optimized web applications with rapid page rendering features from the constructive and interactive technology of React.',
      color: '#61dafb',
    },
    {
      name: 'Next.js',
      icon: SiNextdotjs,
      description:
        'Create production-ready React applications with server-side rendering, static site generation, and optimized performance out of the box.',
      color: '#000000',
    },
  ],
  backend: [
    {
      name: 'Node.js',
      icon: SiNodedotjs,
      description:
        'Build scalable server-side applications with JavaScript runtime that enables efficient handling of concurrent requests and real-time features.',
      color: '#339933',
    },
    {
      name: 'Express.js',
      icon: SiExpress,
      description:
        'Create robust REST APIs and web applications with minimal and flexible Node.js web application framework.',
      color: '#000000',
    },
  ],
  integrations: [
    {
      name: 'Payment Gateway',
      icon: SiStripe,
      description:
        'Secure payment processing with Stripe integration supporting multiple payment methods, currencies, and fraud protection.',
      color: '#635bff',
    },
    {
      name: 'SMS & OTP',
      icon: SiTwilio,
      description:
        'Reliable SMS delivery and OTP verification services for user authentication and communication features.',
      color: '#f22f46',
    },
    {
      name: 'Multi-language',
      icon: FaLanguage,
      description:
        'Comprehensive internationalization support with RTL languages, dynamic content translation, and locale management.',
      color: '#4285f4',
    },
    {
      name: 'Shipping',
      icon: FaShippingFast,
      description:
        'Seamless shipping solutions with real-time tracking, multiple carrier support, and automated delivery notifications for enhanced customer experience.',
      color: '#ff6b35',
    },
    {
      name: 'Tax Integration',
      icon: FaReceipt,
      description:
        "Saudi Arabia's ZATCA e-invoicing compliance with automated tax calculations, invoice generation, and seamless integration with local tax regulations.",
      color: '#00a86b',
    },
  ],
  cloud: [
    {
      name: 'MongoDB Atlas',
      icon: SiMongodb,
      description:
        'Scalable NoSQL database solution with automatic scaling, global clusters, and advanced security features.',
      color: '#47a248',
    },
    {
      name: 'AWS Cloud',
      icon: SiAmazon,
      description:
        'Comprehensive cloud infrastructure with auto-scaling, load balancing, and enterprise-grade security.',
      color: '#ff9900',
    },
    {
      name: 'Docker',
      icon: SiDocker,
      description:
        'Containerized deployment with consistent environments across development, staging, and production.',
      color: '#2496ed',
    },
  ],
};

export default function Section7(): JSX.Element {
  const [value, setValue] = React.useState<string>('1');

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  // Helper function to render technology cards
  const renderTechnologyCards = (technologies: any[]) => {
    return (
      <div
        className={'custom-scroll-bar'}
        style={{
          flexDirection: 'row',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          overflowX: 'auto',
        }}
      >
        {technologies.map((tech, index) => {
          const IconComponent = tech.icon;
          return (
            <Card
              key={index}
              sx={{
                border: '1px solid #d9d9d9',
                borderRadius: 0,
                p: 3,
                pb: 0,
                gap: 2.5,
                display: 'flex',
                alignItems: 'self-start',
                flexDirection: 'column',
                minWidth: '270px',
                height: '370px',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <IconComponent color={tech.color} size={50} style={{ marginBottom: '12px' }} />
                <Typography variant="h4" color="black" className="font-helvetica-bold">
                  {tech.name}
                </Typography>
              </Box>
              <Typography variant="body1" color="#616161" className="font-helvetica">
                {tech.description}
              </Typography>
              <Button
                variant="text"
                className="font-helvetica"
                sx={{ px: 0, color: 'black' }}
              // endIcon={<ArrowForward />}
              >
                {/* Learn more */}

              </Button>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Box sx={{ px: 2, py: 10, pb: 3 }}>
        <Stack
          spacing={1}
          maxWidth={E_COMMERCE_STYLES.container.maxWidth}
          mx={E_COMMERCE_STYLES.container.mx}
        >
          <Typography variant="h3" color="black" className="font-helvetica-bold">
            Built on Scalable Architecture!
          </Typography>
          <Typography
            variant="caption"
            fontSize={13}
            color="black"
            className="font-helvetica-light"
          >
            Your platform should grow with your business — not slow it down. Osperb use a modular,
            microservices-based architecture that supports performance, flexibility, and long-term
            evolution.
          </Typography>
          <Typography
            variant="caption"
            fontSize={13}
            color="black"
            className="font-helvetica-light"
          >
            ⚙️ Scale individual components without affecting the whole system <br />
          </Typography>
          <Typography
            variant="caption"
            fontSize={13}
            color="black"
            className="font-helvetica-light"
          >
            🚀 Handle traffic surges without crashes <br />
          </Typography>
          <Typography
            variant="caption"
            fontSize={13}
            color="black"
            className="font-helvetica-light"
          >
            🔄 Add features without rebuilding the platform <br />
          </Typography>
          <Typography
            variant="caption"
            fontSize={13}
            color="black"
            className="font-helvetica-light"
          >
            🔐 Maintain speed, security, and stability as you grow
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ px: 2, py: 6, backgroundColor: '#f5f5f5' }}>
        <Box maxWidth={E_COMMERCE_STYLES.container.maxWidth} mx={E_COMMERCE_STYLES.container.mx}>
          <Typography variant="body1" fontSize={28} color="black" className="font-helvetica-light">
            Technologies We Use <br /> For E-commerce
          </Typography>

          <Box sx={{ maxWidth: '300px', mb: 2 }}>
            <Tabs
              className="technology-tab"
              value={value}
              onChange={handleChange}
              aria-label="technology tabs"
              sx={{
                minHeight: 36,
                '& .MuiTabs-indicator': { backgroundColor: '#003eff' },
                '& .MuiTab-root': { minHeight: 36, color: 'black' },
                '& .MuiTab-root.Mui-selected': { color: '#003eff' },
              }}
            >
              <Tab
                label="Frontend"
                value="1"
                sx={{ p: 0, minWidth: 'fit-content', fontSize: '14px' }}
              />
              <Tab
                label="Backend"
                value="2"
                sx={{ p: 0, minWidth: 'fit-content', fontSize: '14px' }}
              />
              <Tab
                label="Integrations"
                value="3"
                sx={{ p: 0, minWidth: 'fit-content', fontSize: '14px' }}
              />
              <Tab
                label="Cloud"
                value="4"
                sx={{ p: 0, minWidth: 'fit-content', fontSize: '14px' }}
              />
            </Tabs>
          </Box>
          {value === '1' && (
            <Box role="tabpanel" sx={{ py: 2 }}>
              {renderTechnologyCards(technologyData.frontend)}
            </Box>
          )}
          {value === '2' && (
            <Box role="tabpanel" sx={{ py: 2 }}>
              {renderTechnologyCards(technologyData.backend)}
            </Box>
          )}
          {value === '3' && (
            <Box role="tabpanel" sx={{ py: 2 }}>
              {renderTechnologyCards(technologyData.integrations)}
            </Box>
          )}
          {value === '4' && (
            <Box role="tabpanel" sx={{ py: 2 }}>
              {renderTechnologyCards(technologyData.cloud)}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
